import React from "react";
import PropTypes from "prop-types";
import { Loader, Message, Table } from "semantic-ui-react";
import { getDateFormat } from "../../../../../../../../../common/date-format";

const columns = [
  ["id", "ID"],
  ["name", "Name"],
  ["itemAmount", "Item amount"],
  ["governmentFee", "Gov. fee"],
  ["shippingFee", "Shipping fee"],
  ["quantity", "Quantity"],
  ["serviceSpeed", "Service speed"],
  ["transactionId", "Tran. ID"],
  ["currency", "Currency"],
  ["chargedAmount", "Charged amount"],
  ["purchasedAt", "Purchased at"],
  ["reconciliation", "Reconciliation"],
];

const displayValue = (value, key) => {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }
  if (key === "purchasedAt") {
    return getDateFormat(value);
  }
  return String(value);
};

const rowShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  source: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  itemAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  governmentFee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  shippingFee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serviceSpeed: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  transactionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currency: PropTypes.string,
  chargedAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  purchasedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reconciliation: PropTypes.string,
});

const sourceToSection = {
  Purchase: "entry",
  "Legacy cart": "entry",
  "Applicant cart": "unifiedCart",
  UCC: "unifiedCart",
};

const sectionDefinitions = [
  {
    key: "entry",
    title: "Entry Products",
    description:
      "Review the products and fees recorded when this application was first purchased through Entry.",
  },
  {
    key: "unifiedCart",
    title: "Unified Cart Products",
    description:
      "Review the Unified Cart Admin products and upsells that this application's original Entry purchase does not already cover.",
  },
];

// The Entry section lists the purchase snapshot only, so a source outside that
// allow-list still has to stay visible in the other section rather than being
// dropped from the modal.
const sectionKeyFor = (source) => sourceToSection[source] || "unifiedCart";

const partitionRows = (rows) =>
  rows.reduce(
    (sections, row) => {
      sections[sectionKeyFor(row.source)].push(row);
      return sections;
    },
    { entry: [], unifiedCart: [] },
  );

const renderSection = ({ key, title, description, rows }) => (
  <section
    key={key}
    className="already-charged-items__section"
    aria-labelledby={`already-charged-items-${key}`}
    aria-describedby={`already-charged-items-${key}-description`}
  >
    <h5 id={`already-charged-items-${key}`}>{title}</h5>
    <p id={`already-charged-items-${key}-description`}>{description}</p>
    <div style={{ maxHeight: "400px", overflowY: "auto", overflowX: "auto" }}>
      <Table celled striped fixed className="already-charged-items__table">
        <Table.Header>
          <Table.Row>
            {columns.map(([columnKey, label]) => (
              <Table.HeaderCell
                className="already-charged-items__header"
                key={columnKey}
              >
                {label}
              </Table.HeaderCell>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.length === 0 ? (
            <Table.Row>
              <Table.Cell
                className="already-charged-items__cell"
                colSpan={columns.length}
              >
                No products recorded.
              </Table.Cell>
            </Table.Row>
          ) : (
            rows.map((row) => (
              <Table.Row key={row.key}>
                {columns.map(([columnKey]) => (
                  <Table.Cell
                    className="already-charged-items__cell"
                    key={columnKey}
                  >
                    {displayValue(row[columnKey], columnKey)}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))
          )}
        </Table.Body>
      </Table>
    </div>
  </section>
);

function AlreadyChargedItems({ rows = [], status = "idle", warning = null }) {
  const shouldRender = status !== "idle" || Boolean(warning) || rows.length > 0;
  if (!shouldRender) {
    return null;
  }

  const rowsBySection = partitionRows(rows);

  return (
    <div className="already-charged-items" style={{ marginBottom: "15px" }}>
      <h4 style={{ color: "var(--text-primary, inherit)" }}>
        Already Charged Items:
      </h4>
      {status === "loading" && <Loader active inline="centered" size="small" />}
      {warning && <Message warning content={warning} />}
      {status === "success" && rows.length === 0 && (
        <Message content="No recorded purchases." />
      )}
      {rows.length > 0 &&
        sectionDefinitions.map((section) =>
          renderSection({
            ...section,
            rows: rowsBySection[section.key],
          }),
        )}
    </div>
  );
}

AlreadyChargedItems.propTypes = {
  rows: PropTypes.arrayOf(rowShape),
  status: PropTypes.oneOf(["idle", "loading", "success", "error"]),
  warning: PropTypes.string,
};

export default AlreadyChargedItems;
